(() => {
    "use strict";

    // ==========================================================
    // ROTATOR CONFIGURATION
    // ==========================================================

    const RESIZE_DEBOUNCE_MS = 120;
    const AUTO_ADVANCE_SECONDS = 10; // 0 disables automatic rotation.
    const QUOTE_OUT_MS = 180;
    const QUOTE_IN_MS = 280;

    // HOME ONLY.
    // Books has its own independent setting.
    const ALLOW_RANDOM_EXTRACTED_QUOTES = true;

    // Home Back / Forward history support.
    const HOME_ROTATOR_HISTORY_ENABLED = true;

    // When a chosen chapter contains one or more #rotator-quote
    // selections, this is the chance of using one of Tom's
    // selections instead of making a fresh extraction.
    const RANDOM_QUOTE_MANUAL_CHANCE = 0.5;

    const RANDOM_QUOTE_MIN_WORDS = 18;
    const RANDOM_QUOTE_MAX_WORDS = 80;
    const RANDOM_QUOTE_MAX_SENTENCES = 3;

    // Long passages shrink but are never truncated.
    const SHRINK_SCALES = [0.88, 0.78, 0.70, 0.64];

    // ==========================================================
    // FIND ROTATOR
    // ==========================================================

    const rotator = document.querySelector("[data-home-rotator]");
    if (!rotator) return;

    if (rotator.getAttribute("data-home-rotator-initialized") === "true") {
        console.warn("Rotator skipped duplicate initialization.", rotator);
        return;
    }

    rotator.setAttribute("data-home-rotator-initialized", "true");

    // ==========================================================
    // DOM
    // ==========================================================

    const link = rotator.querySelector("[data-home-rotator-link]");
    const content = rotator.querySelector("[data-home-rotator-content]");
    const quote = rotator.querySelector("[data-home-rotator-quote]");
    const sourceLine = rotator.querySelector("[data-home-rotator-source-line]");
    const sourceTitle = rotator.querySelector("[data-home-rotator-source-title]");
    const bookWrap = rotator.querySelector("[data-home-rotator-book-wrap]");
    const bookTitle = rotator.querySelector("[data-home-rotator-book-title]");

    // ==========================================================
    // STATE
    // ==========================================================

    const reducedMotion = window
        .matchMedia("(prefers-reduced-motion: reduce)")
        .matches;

    let chapters = [];
    let availableChapters = [];
    let currentChapterKey = "";
    let currentQuoteText = "";
    let busy = false;
    let paused = false;
    let autoAdvanceTimer = null;
    let resizeTimer = null;

    // Back / Forward history.
    let quoteHistory = [];
    let quoteHistoryIndex = -1;

    const postDocumentCache = new Map();

    // ==========================================================
    // GENERAL HELPERS
    // ==========================================================

    function shuffle(values) {
        const copy = [...values];

        for (let index = copy.length - 1; index > 0; index -= 1) {
            const randomIndex = Math.floor(Math.random() * (index + 1));

            [copy[index], copy[randomIndex]] = [
                copy[randomIndex],
                copy[index]
            ];
        }

        return copy;
    }

    function wait(milliseconds) {
        if (!milliseconds || milliseconds <= 0) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            window.setTimeout(resolve, milliseconds);
        });
    }

    function waitForWindowLoad() {
        if (document.readyState === "complete") {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            window.addEventListener("load", resolve, {once: true});
        });
    }

    function waitForFonts() {
        if (!document.fonts?.ready) {
            return Promise.resolve();
        }

        return document.fonts.ready.catch(() => undefined);
    }

    function normalizeText(text) {
        return String(text || "")
            .replace(/\s+/g, " ")
            .trim();
    }

    function countWords(text) {
        const words = normalizeText(text).match(/\S+/g);
        return words ? words.length : 0;
    }

    function normalizeBookTitle(title) {
        return String(title || "")
            .replace(/^(?:Book|FamBook)\s*:\s*/i, "")
            .trim();
    }

    function getChapterKey(chapter) {
        return [
            chapter?.bookSlug || "",
            chapter?.chapterSlug || ""
        ].join("::");
    }

    function getBookReaderUrl(bookSlug, chapterSlug) {
        if (!bookSlug || !chapterSlug) return "#";

        return (
            "/books/?book=" +
            encodeURIComponent(bookSlug) +
            "&chapter=" +
            encodeURIComponent(chapterSlug)
        );
    }

    // ==========================================================
    // LIVE CHAPTER CATALOG
    // ==========================================================

    async function loadLiveChapterCatalog() {
        const catalog =
            window.frBooksCatalog;

        if (
            !catalog ||
            typeof catalog.getBooks !== "function"
        ) {
            throw new Error(
                "Authorized Books catalog was not found."
            );
        }

        if (catalog.ready) {
            await catalog.ready;
        }

        const result = [];
        const knownKeys = new Set();

        const books = catalog.getBooks();

        books.forEach((book) => {
            const bookSlug = String(
                book?.slug || ""
            ).trim();

            const displayBookTitle = normalizeBookTitle(
                book?.title ||
                book?.tagName ||
                ""
            );

            const bookChapters = Array.isArray(
                book?.chapters
            )
                ? book.chapters
                : [];

            bookChapters.forEach((chapterData) => {
                const chapterSlug = String(
                    chapterData?.slug || ""
                ).trim();

                const chapterUrl = String(
                    chapterData?.url || ""
                ).trim();

                const chapterTitle = String(
                    chapterData?.title || ""
                ).trim();

                if (
                    !bookSlug ||
                    !chapterSlug ||
                    !chapterUrl ||
                    !chapterTitle
                ) {
                    return;
                }

                const chapter = {
                    chapterId: String(
                        chapterData?.id || ""
                    ).trim(),

                    chapterSlug,
                    chapterTitle,
                    chapterUrl,
                    bookSlug,
                    bookTitle: displayBookTitle
                };

                const key = getChapterKey(chapter);

                if (knownKeys.has(key)) return;

                knownKeys.add(key);
                result.push(chapter);
            });
        });

        console.info(
            "Home rotator authorized Books catalog supplied:",
            result.length,
            "chapters"
        );

        return result;
    }

    // ==========================================================
    // MANUAL-QUOTE CHAPTER INDEX
    // ==========================================================

    function loadManualQuoteChapterIndex() {
        const catalog = document.getElementById(
            "fr-rotator-quote-catalog"
        );

        const keys = new Set();

        if (!catalog) return keys;

        const records = Array.from(
            catalog.querySelectorAll(
                "[data-fr-rotator-quote-item]"
            )
        );

        records.forEach((record) => {
            try {
                const item = JSON.parse(
                    record.textContent || ""
                );

                const itemUrl = String(
                    item?.url || ""
                ).trim();

                if (!itemUrl) return;

                const parsed = new URL(
                    itemUrl,
                    window.location.origin
                );

                const bookSlug = String(
                    parsed.searchParams.get("book") || ""
                ).trim();

                const chapterSlug = String(
                    parsed.searchParams.get("chapter") || ""
                ).trim();

                if (!bookSlug || !chapterSlug) return;

                keys.add(
                    `${bookSlug}::${chapterSlug}`
                );
            } catch (error) {
                console.warn(
                    "Unable to read manual rotator chapter index:",
                    error
                );
            }
        });

        return keys;
    }

    // ==========================================================
    // LIVE POST FETCH
    // ==========================================================

    function fetchPostDocument(chapterUrl) {
        const url = String(chapterUrl || "").trim();

        if (!url) {
            return Promise.reject(
                new Error("Chapter URL is missing.")
            );
        }

        if (postDocumentCache.has(url)) {
            return postDocumentCache.get(url);
        }

        const request = fetch(url, {
            credentials: "same-origin",
            cache: "no-store"
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Chapter request failed: ${response.status}`
                    );
                }

                return response.text();
            })
            .then((html) => {
                return new DOMParser().parseFromString(
                    html,
                    "text/html"
                );
            })
            .catch((error) => {
                postDocumentCache.delete(url);
                throw error;
            });

        postDocumentCache.set(url, request);

        return request;
    }

    // ==========================================================
    // POST CONTENT
    // ==========================================================

    function getPostContent(postDocument) {
        return (
            postDocument.querySelector(".gh-content") ||
            postDocument.querySelector(".kg-post-content") ||
            postDocument.querySelector("article")
        );
    }

    // ==========================================================
    // MANUAL #rotator-quote EXTRACTION
    // ==========================================================

    function isRotatorQuoteAnchor(anchor) {
        const href = String(
            anchor?.getAttribute("href") || ""
        ).trim();

        if (!href) return false;

        try {
            return (
                new URL(
                    href,
                    window.location.href
                ).hash === "#rotator-quote"
            );
        } catch (error) {
            return (
                href === "#rotator-quote" ||
                href.endsWith("#rotator-quote")
            );
        }
    }

    function getManualRotatorQuotes(postDocument) {
        const postContent = getPostContent(postDocument);
        if (!postContent) return [];

        return Array.from(
            postContent.querySelectorAll("a[href]")
        )
            .filter(isRotatorQuoteAnchor)
            .map((anchor) => normalizeText(
                anchor.textContent
            ))
            .filter(Boolean);
    }

    // ==========================================================
    // AUTOMATIC LIVE EXTRACTION
    // ==========================================================

    function splitIntoSentences(text) {
        const normalized = normalizeText(text);
        if (!normalized) return [];

        if (
            typeof Intl !== "undefined" &&
            typeof Intl.Segmenter === "function"
        ) {
            const segmenter = new Intl.Segmenter(
                "en",
                {granularity: "sentence"}
            );

            return Array.from(
                segmenter.segment(normalized)
            )
                .map((part) => normalizeText(
                    part.segment
                ))
                .filter(Boolean);
        }

        return normalized
            .split(
                /(?<=[.!?])\s+(?=[A-Z0-9“‘"'])/
            )
            .map(normalizeText)
            .filter(Boolean);
    }

    function getAutomaticQuoteParagraphs(postDocument) {
        const postContent = getPostContent(postDocument);
        if (!postContent) return [];

        return Array.from(
            postContent.querySelectorAll("p, blockquote")
        )
            .filter((element) => {
                if (
                    element.matches("blockquote") &&
                    element.querySelector("p")
                ) {
                    return false;
                }

                return !element.closest([
                    "figure",
                    ".kg-card",
                    "script",
                    "style",
                    "template",
                    "noscript"
                ].join(","));
            })
            .map((element) => normalizeText(
                element.textContent
            ))
            .filter((text) => {
                return countWords(text) >= 6;
            });
    }

    function getEndingParagraphSkipCount(paragraphCount) {
        if (paragraphCount >= 8) return 3;
        if (paragraphCount >= 5) return 2;
        if (paragraphCount >= 3) return 1;
        return 0;
    }

    function buildAutomaticQuoteCandidates(postDocument) {
        const paragraphs =
            getAutomaticQuoteParagraphs(postDocument);

        if (!paragraphs.length) return [];

        const endingSkipCount =
            getEndingParagraphSkipCount(
                paragraphs.length
            );

        const usableParagraphCount = Math.max(
            0,
            paragraphs.length - endingSkipCount
        );

        const candidates = [];
        let openingSentenceSkipped = false;

        for (
            let paragraphIndex = 0;
            paragraphIndex < usableParagraphCount;
            paragraphIndex += 1
        ) {
            let sentences = splitIntoSentences(
                paragraphs[paragraphIndex]
            );

            if (
                !openingSentenceSkipped &&
                sentences.length
            ) {
                sentences = sentences.slice(1);
                openingSentenceSkipped = true;
            }

            if (!sentences.length) continue;

            for (
                let startIndex = 0;
                startIndex < sentences.length;
                startIndex += 1
            ) {
                for (
                    let sentenceCount = 1;
                    sentenceCount <= RANDOM_QUOTE_MAX_SENTENCES;
                    sentenceCount += 1
                ) {
                    const endIndex =
                        startIndex + sentenceCount;

                    if (endIndex > sentences.length) {
                        break;
                    }

                    const quoteText = normalizeText(
                        sentences
                            .slice(startIndex, endIndex)
                            .join(" ")
                    );

                    const wordCount =
                        countWords(quoteText);

                    if (
                        wordCount >
                        RANDOM_QUOTE_MAX_WORDS
                    ) {
                        break;
                    }

                    if (
                        wordCount >=
                        RANDOM_QUOTE_MIN_WORDS
                    ) {
                        candidates.push(quoteText);
                    }
                }
            }
        }

        return candidates;
    }

    // ==========================================================
    // SIZE / FIT
    // ==========================================================

    function syncFixedHeight() {
        if (!link) return;

        rotator.style.height = "";
        link.style.height = "";
        link.style.minHeight = "";

        const minimumHeight =
            window.getComputedStyle(rotator).minHeight;

        if (
            !minimumHeight ||
            minimumHeight === "auto" ||
            minimumHeight === "0px"
        ) {
            return;
        }

        rotator.style.height = minimumHeight;
        link.style.height = "100%";
        link.style.minHeight = "0";
    }

    function quoteFits() {
        if (!content || !link) return true;

        const styles =
            window.getComputedStyle(link);

        const paddingTop =
            parseFloat(styles.paddingTop) || 0;

        const paddingBottom =
            parseFloat(styles.paddingBottom) || 0;

        const availableHeight = Math.max(
            0,
            link.clientHeight -
            paddingTop -
            paddingBottom
        );

        return (
            content.scrollHeight <=
            availableHeight + 1
        );
    }

    function fitQuote(fullText) {
        if (!quote) return true;

        const cleanText =
            String(fullText || "").trim();

        quote.style.fontSize = "";
        quote.textContent = cleanText;

        if (!cleanText || quoteFits()) {
            return true;
        }

        const baseFontSize = parseFloat(
            window.getComputedStyle(quote).fontSize
        );

        if (
            Number.isFinite(baseFontSize) &&
            baseFontSize > 0
        ) {
            for (const scale of SHRINK_SCALES) {
                quote.style.fontSize =
                    `${baseFontSize * scale}px`;

                if (quoteFits()) {
                    return true;
                }
            }
        }

        return false;
    }

    function itemCanDisplay(item) {
        if (!quote) return true;

        const savedText = quote.textContent;
        const savedFontSize =
            quote.style.fontSize;

        const fits = fitQuote(
            item.text || ""
        );

        quote.textContent = savedText;
        quote.style.fontSize = savedFontSize;

        return fits;
    }

    function refitCurrentQuote() {
        if (
            rotator.offsetParent === null ||
            !currentQuoteText
        ) {
            return;
        }

        fitQuote(currentQuoteText);
    }

    // ==========================================================
    // BUILD QUOTE ITEMS
    // ==========================================================

    function buildQuoteItem(
        chapter,
        text,
        type
    ) {
        const extracted =
            type === "extracted";

        return {
            id: [
                type,
                chapter.bookSlug,
                chapter.chapterSlug,
                Math.random()
                    .toString(36)
                    .slice(2)
            ].join(":"),

            chapterKey:
                getChapterKey(chapter),

            text: extracted
                ? `…${normalizeText(text).replace(/\.+$/, "")}…`
                : normalizeText(text),

            sourceTitle:
            chapter.chapterTitle,

            bookTitle:
            chapter.bookTitle,

            url: getBookReaderUrl(
                chapter.bookSlug,
                chapter.chapterSlug
            ),

            type
        };
    }

    function findDisplayableManualQuote(
        chapter,
        manualQuotes
    ) {
        const candidates =
            shuffle(manualQuotes);

        for (const text of candidates) {
            const item = buildQuoteItem(
                chapter,
                text,
                "manual"
            );

            if (itemCanDisplay(item)) {
                return item;
            }
        }

        return null;
    }

    function findDisplayableAutomaticQuote(
        chapter,
        automaticQuotes
    ) {
        const candidates =
            shuffle(automaticQuotes);

        for (const text of candidates) {
            const item = buildQuoteItem(
                chapter,
                text,
                "extracted"
            );

            if (itemCanDisplay(item)) {
                return item;
            }
        }

        return null;
    }

    function chooseQuoteFromPost(
        chapter,
        postDocument
    ) {
        const manualQuotes =
            getManualRotatorQuotes(
                postDocument
            );

        if (!ALLOW_RANDOM_EXTRACTED_QUOTES) {
            return findDisplayableManualQuote(
                chapter,
                manualQuotes
            );
        }

        const automaticQuotes =
            buildAutomaticQuoteCandidates(
                postDocument
            );

        if (manualQuotes.length) {
            const useManual =
                Math.random() <
                RANDOM_QUOTE_MANUAL_CHANCE;

            if (useManual) {
                return (
                    findDisplayableManualQuote(
                        chapter,
                        manualQuotes
                    ) ||
                    findDisplayableAutomaticQuote(
                        chapter,
                        automaticQuotes
                    )
                );
            }

            return (
                findDisplayableAutomaticQuote(
                    chapter,
                    automaticQuotes
                ) ||
                findDisplayableManualQuote(
                    chapter,
                    manualQuotes
                )
            );
        }

        return findDisplayableAutomaticQuote(
            chapter,
            automaticQuotes
        );
    }

    // ==========================================================
    // CHAPTER SELECTION
    // ==========================================================

    function refillAvailableChapters() {
        const alternatives =
            chapters.filter((chapter) => {
                return (
                    getChapterKey(chapter) !==
                    currentChapterKey
                );
            });

        availableChapters = shuffle(
            alternatives.length
                ? alternatives
                : chapters
        );
    }

    function takeNextChapter() {
        if (!chapters.length) return null;

        if (!availableChapters.length) {
            refillAvailableChapters();
        }

        return (
            availableChapters.shift() ||
            null
        );
    }

    async function loadQuoteForChapter(chapter) {
        if (!chapter?.chapterUrl) {
            return null;
        }

        try {
            const postDocument =
                await fetchPostDocument(
                    chapter.chapterUrl
                );

            return chooseQuoteFromPost(
                chapter,
                postDocument
            );
        } catch (error) {
            console.warn(
                "Home rotator could not load chapter:",
                chapter.chapterUrl,
                error
            );

            return null;
        }
    }

    async function nextItem() {
        if (!chapters.length) return null;

        let attempts = chapters.length;

        while (attempts > 0) {
            const chapter =
                takeNextChapter();

            if (!chapter) return null;

            const item =
                await loadQuoteForChapter(
                    chapter
                );

            if (item) {
                return item;
            }

            attempts -= 1;
        }

        return null;
    }

    // ==========================================================
    // HISTORY
    // ==========================================================

    function resetHistory() {
        quoteHistory = [];
        quoteHistoryIndex = -1;
        publishHistoryState();
    }

    function pushHistoryItem(item) {
        if (!HOME_ROTATOR_HISTORY_ENABLED || !item) {
            return;
        }

        if (
            quoteHistoryIndex <
            quoteHistory.length - 1
        ) {
            quoteHistory = quoteHistory.slice(
                0,
                quoteHistoryIndex + 1
            );
        }

        quoteHistory.push(item);
        quoteHistoryIndex =
            quoteHistory.length - 1;

        publishHistoryState();
    }

    function canGoBack() {
        return (
            HOME_ROTATOR_HISTORY_ENABLED &&
            quoteHistoryIndex > 0
        );
    }

    function canGoForwardInHistory() {
        return (
            HOME_ROTATOR_HISTORY_ENABLED &&
            quoteHistoryIndex >= 0 &&
            quoteHistoryIndex <
            quoteHistory.length - 1
        );
    }

    function getPreviousHistoryItem() {
        if (!canGoBack()) return null;

        quoteHistoryIndex -= 1;
        publishHistoryState();

        return (
            quoteHistory[quoteHistoryIndex] ||
            null
        );
    }

    function getNextHistoryItem() {
        if (!canGoForwardInHistory()) {
            return null;
        }

        quoteHistoryIndex += 1;
        publishHistoryState();

        return (
            quoteHistory[quoteHistoryIndex] ||
            null
        );
    }

    function publishHistoryState() {
        const detail = {
            enabled:
            HOME_ROTATOR_HISTORY_ENABLED,

            canGoBack:
                canGoBack(),

            canGoForward:
                canGoForwardInHistory(),

            index:
            quoteHistoryIndex,

            length:
            quoteHistory.length
        };

        rotator.setAttribute(
            "data-home-rotator-can-go-back",
            detail.canGoBack
                ? "true"
                : "false"
        );

        rotator.setAttribute(
            "data-home-rotator-can-go-forward",
            detail.canGoForward
                ? "true"
                : "false"
        );

        rotator.dispatchEvent(
            new CustomEvent(
                "fr-home-rotator-history-change",
                {detail}
            )
        );
    }

    // ==========================================================
    // LINK
    // ==========================================================

    function setLink(item) {
        if (!link) return;

        if (item.url) {
            link.setAttribute(
                "href",
                item.url
            );

            link.removeAttribute(
                "aria-disabled"
            );
        } else {
            link.removeAttribute("href");

            link.setAttribute(
                "aria-disabled",
                "true"
            );
        }
    }

    // ==========================================================
    // ANIMATION
    // ==========================================================

    function showPassageImmediately() {
        if (!link) return;

        link.style.transition = "none";
        link.style.opacity = "1";
    }

    async function animatePassageOut() {
        if (!link) return;

        link.style.transition = "none";
        link.style.opacity = "1";

        void link.offsetWidth;

        if (
            reducedMotion ||
            QUOTE_OUT_MS <= 0
        ) {
            link.style.opacity = "0";
            return;
        }

        link.style.transition =
            `opacity ${QUOTE_OUT_MS}ms ease`;

        link.style.opacity = "0";

        await wait(QUOTE_OUT_MS);
    }

    async function animatePassageIn() {
        if (!link) return;

        link.style.transition = "none";
        link.style.opacity = "0";

        void link.offsetWidth;

        if (
            reducedMotion ||
            QUOTE_IN_MS <= 0
        ) {
            link.style.opacity = "1";
            return;
        }

        link.style.transition =
            `opacity ${QUOTE_IN_MS}ms ease`;

        link.style.opacity = "1";

        await wait(QUOTE_IN_MS);
    }

    // ==========================================================
    // RENDER
    // ==========================================================

    function renderItem(item) {
        currentQuoteText =
            item.text || "";

        if (quote) {
            quote.hidden = false;
        }

        if (
            sourceTitle &&
            sourceLine
        ) {
            sourceTitle.textContent =
                item.sourceTitle
                    ? `“${item.sourceTitle}”`
                    : "";

            sourceLine.hidden =
                !item.sourceTitle;
        }

        if (
            bookWrap &&
            bookTitle
        ) {
            bookTitle.textContent =
                item.bookTitle || "";

            bookWrap.hidden =
                !item.bookTitle;
        }

        setLink(item);

        if (!fitQuote(currentQuoteText)) {
            return false;
        }

        currentChapterKey =
            item.chapterKey || "";

        rotator.setAttribute(
            "data-home-rotator-current-id",
            item.id || ""
        );

        rotator.setAttribute(
            "data-home-rotator-quote-type",
            item.type || ""
        );

        return true;
    }

    async function transitionToItem(item) {
        if (!item) return false;

        await animatePassageOut();

        if (!renderItem(item)) {
            console.warn(
                "Home rotator: passage could not be rendered."
            );

            await animatePassageIn();
            return false;
        }

        await animatePassageIn();
        return true;
    }

    // ==========================================================
    // AUTO ADVANCE
    // ==========================================================

    function cancelAutoAdvance() {
        if (!autoAdvanceTimer) return;

        window.clearTimeout(
            autoAdvanceTimer
        );

        autoAdvanceTimer = null;
    }

    function scheduleAutoAdvance() {
        cancelAutoAdvance();

        if (
            paused ||
            AUTO_ADVANCE_SECONDS <= 0
        ) {
            return;
        }

        autoAdvanceTimer =
            window.setTimeout(() => {
                autoAdvanceTimer = null;
                requestForward();
            }, AUTO_ADVANCE_SECONDS * 1000);
    }

    // ==========================================================
    // NEW RANDOM PASSAGE
    // ==========================================================

    async function requestNewItem() {
        const item = await nextItem();

        if (!item) {
            console.warn(
                "Home rotator: no usable live passage was found."
            );

            return false;
        }

        const rendered =
            await transitionToItem(item);

        if (!rendered) {
            return false;
        }

        pushHistoryItem(item);
        return true;
    }

    // ==========================================================
    // BACK
    // ==========================================================

    async function requestBack() {
        if (
            busy ||
            !HOME_ROTATOR_HISTORY_ENABLED ||
            !canGoBack()
        ) {
            return;
        }

        busy = true;
        cancelAutoAdvance();

        const previousIndex =
            quoteHistoryIndex;

        try {
            const item =
                getPreviousHistoryItem();

            if (!item) return;

            const rendered =
                await transitionToItem(item);

            if (!rendered) {
                quoteHistoryIndex =
                    previousIndex;

                publishHistoryState();
            }
        } catch (error) {
            quoteHistoryIndex =
                previousIndex;

            publishHistoryState();

            console.error(
                "Home rotator back failed:",
                error
            );

            showPassageImmediately();
        } finally {
            busy = false;
            scheduleAutoAdvance();
        }
    }

    // ==========================================================
    // FORWARD
    // ==========================================================

    async function requestForward() {
        if (busy) return;

        busy = true;
        cancelAutoAdvance();

        try {
            if (
                HOME_ROTATOR_HISTORY_ENABLED &&
                canGoForwardInHistory()
            ) {
                const previousIndex =
                    quoteHistoryIndex;

                const item =
                    getNextHistoryItem();

                if (item) {
                    const rendered =
                        await transitionToItem(item);

                    if (!rendered) {
                        quoteHistoryIndex =
                            previousIndex;

                        publishHistoryState();
                    }
                }

                return;
            }

            await requestNewItem();
        } catch (error) {
            console.error(
                "Home rotator forward failed:",
                error
            );

            showPassageImmediately();
        } finally {
            busy = false;
            scheduleAutoAdvance();
        }
    }

    // ==========================================================
    // EVENTS
    // ==========================================================

    function wireEvents() {
        rotator.addEventListener(
            "fr-home-rotator-back",
            requestBack
        );

        rotator.addEventListener(
            "fr-home-rotator-forward",
            requestForward
        );

        rotator.addEventListener(
            "mouseenter",
            () => {
                paused = true;
                cancelAutoAdvance();
            }
        );

        rotator.addEventListener(
            "mouseleave",
            () => {
                paused = false;
                scheduleAutoAdvance();
            }
        );

        rotator.addEventListener(
            "focusin",
            () => {
                paused = true;
                cancelAutoAdvance();
            }
        );

        rotator.addEventListener(
            "focusout",
            (event) => {
                if (
                    rotator.contains(
                        event.relatedTarget
                    )
                ) {
                    return;
                }

                paused = false;
                scheduleAutoAdvance();
            }
        );

        document.addEventListener(
            "visibilitychange",
            () => {
                if (document.hidden) {
                    cancelAutoAdvance();
                } else {
                    scheduleAutoAdvance();
                }
            }
        );

        window.addEventListener(
            "resize",
            () => {
                window.clearTimeout(
                    resizeTimer
                );

                resizeTimer =
                    window.setTimeout(() => {
                        syncFixedHeight();
                        refitCurrentQuote();
                    }, RESIZE_DEBOUNCE_MS);
            }
        );
    }

    // ==========================================================
    // INITIALIZE
    // ==========================================================

    async function initialize() {
        resetHistory();

        const liveChapters =
            await loadLiveChapterCatalog();

        if (!liveChapters.length) {
            console.warn(
                "Home rotator: live chapter catalog contains no chapters."
            );

            return;
        }

        if (ALLOW_RANDOM_EXTRACTED_QUOTES) {
            chapters = liveChapters;
        } else {
            const manualChapterIndex =
                loadManualQuoteChapterIndex();

            const indexedChapters =
                liveChapters.filter((chapter) => {
                    return manualChapterIndex.has(
                        getChapterKey(chapter)
                    );
                });

            chapters = indexedChapters.length
                ? indexedChapters
                : liveChapters;
        }

        console.info(
            "Home rotator eligible chapter pool:",
            chapters.length,
            "chapters",
            ALLOW_RANDOM_EXTRACTED_QUOTES
                ? "(live extraction ON)"
                : "(live extraction OFF)"
        );

        await Promise.all([
            waitForWindowLoad(),
            waitForFonts()
        ]);

        syncFixedHeight();
        refillAvailableChapters();

        const firstItem =
            await nextItem();

        if (!firstItem) {
            console.warn(
                "Home rotator: no usable live passage was found."
            );

            return;
        }

        if (!renderItem(firstItem)) {
            console.warn(
                "Home rotator: initial passage could not be rendered."
            );

            return;
        }

        pushHistoryItem(firstItem);

        showPassageImmediately();
        wireEvents();
        scheduleAutoAdvance();
    }

    initialize().catch((error) => {
        rotator.removeAttribute(
            "data-home-rotator-initialized"
        );

        console.error(
            "Home rotator could not load:",
            error
        );
    });
})();